"use client";

import { cn, formatImageUrl } from "@/lib/utils";
import { TPostMedia } from "@/types/post";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { FeedMediaCarouselModal } from "./feed-image-carousel-modal";

type FeedPostGridProps = {
	items: TPostMedia[];
};

const FeedPostGrid = ({ items }: FeedPostGridProps) => {
	const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

	const openModal = (index: number) => {
		setSelectedImageIndex(index);
	};

	const closeModal = useCallback(() => {
		setSelectedImageIndex(null);
	}, []);

	useEffect(() => {
		const handleEscKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				closeModal();
			}
		};

		if (selectedImageIndex !== null) {
			document.addEventListener("keydown", handleEscKey);
		}

		return () => {
			document.removeEventListener("keydown", handleEscKey);
		};
	}, [selectedImageIndex, closeModal]);

	if (items.length === 0) return null;

	return (
		<div className="w-full">
			{/* Image Grid */}
			<div className="grid aspect-[4/3] max-h-80 w-full grid-cols-2 grid-rows-2 gap-2 2xl:max-h-96">
				{items.slice(0, 4).map((item, index) => (
					<div
						key={index}
						className={cn(
							"relative cursor-pointer",
							index === 0 ? "col-span-1 row-span-2" : "col-span-1 row-span-1"
						)}
						onClick={() => openModal(index)}
					>
						<Image
							fill
							src={formatImageUrl(item.objectPath)}
							alt={item.alt ?? ""}
							className="object-cover"
						/>

						{/* Show +n overlay on the last visible image if there are more */}
						{index === 2 && items.length > 3 && (
							<div className="absolute inset-0 flex items-center justify-center bg-black/40">
								<span className="text-xl font-medium text-white">+{items.length - 3}</span>
							</div>
						)}
					</div>
				))}
			</div>

			{/* Full Screen Modal */}
			{selectedImageIndex !== null && (
				<FeedMediaCarouselModal
					links={[]}
					items={items}
					onCloseModal={closeModal}
					selectedItemIndex={selectedImageIndex}
				/>
			)}
		</div>
	);
};

export default FeedPostGrid;
